import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { Stack } from "@mui/material"

import { MeetingServiceConfig, DynamicConfigKey, MeetingServiceTabValues } from "../form/formTypes"
import { MeetingServiceTimeErrorDialog } from "./orderSummary/MeetingServiceTimeErrorDialog"
import { DimensionsGuardManager } from "./fieldGuards/DimensionsGuardManager"
import { ConfirmOrderSubmitButton } from "../ConfirmOrderSubmitButton"
import { BillReceiverGuard } from "./fieldGuards/BillReceiverGuard"
import { CreateOrderDialogMode, SelectedMainUserState } from "../../createOrderTypes"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { INVALID_DELIVERIES, isMeetingTimeKey } from "../form/helpers"
import { useMeetingTimeRequired } from "./components/useMeetingTimeRequired"
import { DrawerSections } from "./DrawerSections"
import { AutoAddedServicesNotifications } from "./messages/AutoAddedServicesNotifications"
import { RemovedServicesNotification } from "./messages/RemovedServicesNotification"
import { Messages } from "./messages/Messages"

export const DRAWER_WIDTH = "27.5rem"

const defaultDialogState = {
    show: false,
    times: [],
}

const useStyles = makeStyles()((theme) => ({
    content: {
        borderLeft: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.common.white,
        padding: "1rem",
        justifyContent: "space-between",
        height: "100%",
        overflow: "hidden",
    },
    container: {
        overflow: "auto",
        flexGrow: 1,
    },
}))

type DrawerContentProps = Readonly<{
    meetingServiceConfiguration: MeetingServiceConfig
    user: SelectedMainUserState
    mode: CreateOrderDialogMode
    merchantId?: number
}>

export function DrawerContent({ meetingServiceConfiguration, user, mode, merchantId }: DrawerContentProps) {
    const { classes } = useStyles()
    const { sections } = meetingServiceConfiguration
    const { errors, setFieldValue, isSubmitting } = useFormikContext<MeetingServiceTabValues>()
    const { isRefundOrder } = useCreateOrderDialogType()
    const hasMeetingTimeRequired = useMeetingTimeRequired()

    const [expandedAccordionKey, setExpandedAccordionKey] = useState<string | null>(
        mode === CreateOrderDialogMode.CREATE ? "section-0" : null,
    )
    const [showInvalidTimesModal, setShowInvalidTimesModal] = useState<{ show: boolean; times: string[] }>(
        defaultDialogState,
    )

    const wasSubmittingRef = useRef(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const visibleSections = useMemo(() => sections.filter(({ showInDrawer }) => showInDrawer !== false), [sections])

    const scrollToFirstError = useCallback(() => {
        const ACCORDION_ANIMATION_MS = 350
        setTimeout(() => {
            const errorEl = containerRef.current?.querySelector(".Mui-error")
            errorEl?.scrollIntoView({ behavior: "smooth", block: "center" })
        }, ACCORDION_ANIMATION_MS)
    }, [])

    useEffect(() => {
        if (isSubmitting) {
            wasSubmittingRef.current = true
            return
        }
        if (!wasSubmittingRef.current || Object.keys(errors).length === 0) return
        wasSubmittingRef.current = false

        const errorKeys = Object.keys(errors)

        if (errorKeys.includes(DynamicConfigKey.MULTIPLE_DELIVERIES)) {
            const invalidTimes = errors[DynamicConfigKey.MULTIPLE_DELIVERIES]
            if (typeof invalidTimes === "string" && invalidTimes.startsWith(INVALID_DELIVERIES)) {
                const times = invalidTimes.replace(INVALID_DELIVERIES, "").split(",") || []
                if (times.length > 0) {
                    setShowInvalidTimesModal({
                        show: true,
                        times,
                    })
                }
            }
            return
        }

        const DELIVERY_SECTION_KEY = "section-1"
        const deferredToDeliveryKeys = new Set<string>([
            DynamicConfigKey.DELIVERY_LOCATIONS,
            DynamicConfigKey.OTHER_DELIVERY_LOCATION,
        ])
        // The meeting time pair only lives in the delivery section while an extra service requires it.
        if (hasMeetingTimeRequired) {
            deferredToDeliveryKeys.add(DynamicConfigKey.MEETING_TIME)
            deferredToDeliveryKeys.add(DynamicConfigKey.MEETING_START_TIME)
            deferredToDeliveryKeys.add(DynamicConfigKey.MEETING_END_TIME)
        }

        if (errorKeys.some((k) => deferredToDeliveryKeys.has(k))) {
            setExpandedAccordionKey(DELIVERY_SECTION_KEY)
            scrollToFirstError()
            return
        }

        // A legacy MEETING_TIME config renders the same pair, so its section owns start/end errors too.
        const hasMeetingTimeError = errorKeys.some(isMeetingTimeKey)

        for (let i = 0; i < visibleSections.length; i++) {
            if (
                visibleSections[i].groups.some((g) =>
                    g.fields.some((f) => errorKeys.includes(f.key) || (hasMeetingTimeError && isMeetingTimeKey(f.key))),
                )
            ) {
                setExpandedAccordionKey(`section-${i}`)
                scrollToFirstError()
                return
            }
        }
    }, [isSubmitting, errors, visibleSections, hasMeetingTimeRequired])

    const handleDialogClose = () => {
        if (showInvalidTimesModal.times.length > 0) {
            const firstInvalidTime = showInvalidTimesModal.times[0]
            setFieldValue(`${DynamicConfigKey.MULTIPLE_DELIVERIES}.activeTime`, firstInvalidTime)
        }
        setShowInvalidTimesModal(defaultDialogState)
    }

    return (
        <Stack gap="1.5rem" className={classes.content}>
            <Stack ref={containerRef} gap=".75rem" className={classes.container}>
                <RemovedServicesNotification />
                <AutoAddedServicesNotifications />
                <Messages />
                <DrawerSections
                    meetingServiceConfiguration={meetingServiceConfiguration}
                    expandedAccordionKey={expandedAccordionKey}
                    setExpandedAccordionKey={setExpandedAccordionKey}
                    user={user}
                    merchantId={merchantId}
                />
            </Stack>
            <ConfirmOrderSubmitButton isRefundOrder={isRefundOrder} />

            {showInvalidTimesModal.show && (
                <MeetingServiceTimeErrorDialog
                    show={showInvalidTimesModal.show}
                    times={showInvalidTimesModal.times}
                    handleDialogClose={handleDialogClose}
                />
            )}

            {mode !== CreateOrderDialogMode.CREATE && (
                <>
                    <DimensionsGuardManager userId={user.userId} />
                    <BillReceiverGuard userId={user.userId} />
                </>
            )}
        </Stack>
    )
}
